import React, { forwardRef, useMemo } from 'react'

const ContinuedToAndFromDiv = forwardRef(({ currentText, showAsBox = false, remainingSpace = 0, pageNumber }, ref) => {
  // Minimum space threshold to show the box (200px)
  // If space is less than this, only show the text without the box
  const MIN_SPACE_FOR_BOX = 200
  const hasSignificantSpace = showAsBox && remainingSpace > MIN_SPACE_FOR_BOX
  
  // Generate unique ID for this box instance based on page number and remainingSpace
  // This ensures each page gets a truly unique identifier
  // Add timestamp to ensure uniqueness even if pageNumber and remainingSpace are the same
  const uniqueBoxId = useMemo(() => 
    `continued-box-page-${pageNumber || 'unknown'}-space-${Math.round(remainingSpace)}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, 
    [pageNumber, remainingSpace]
  )
  
  // Calculate box height from remainingSpace - only used when box is shown
  const boxHeight = useMemo(() => {
    if (!hasSignificantSpace) return 0
    // Use the actual remaining space height, subtract padding (20px top + 20px bottom = 40px)
    return Math.max(remainingSpace - 40, 0)
  }, [hasSignificantSpace, remainingSpace])
  
  // Always show the text - it's required for continuation indication
  const textContent = currentText || 'Continued on next page'
  
  // If there's significant space, show as a box
  if (hasSignificantSpace) {
    return (
      <div 
        ref={ref}
        id={uniqueBoxId}
        data-box-id={uniqueBoxId}
        data-page-number={pageNumber}
        data-box-height={boxHeight}
        data-remaining-space={remainingSpace}
        className={`continued-on-next-page-box-page-${pageNumber}`}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: `${boxHeight}px`,
          minHeight: '0px',
          maxHeight: `${boxHeight}px`,
          border: '2px solid #333',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          padding: '20px',
          margin: '0',
          marginTop: '20px',
          width: '100%',
          boxSizing: 'border-box',
          pageBreakInside: 'avoid',
          breakInside: 'avoid',
          flex: '0 0 auto',
          flexShrink: 0,
          flexGrow: 0,
          overflow: 'visible',
          position: 'relative',
          zIndex: 0,
          order: 999, // Ensure it appears after all content
          // Prevent overlap with other boxes
          isolation: 'isolate',
        }}
      >
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '16px',
            color: '#333',
            textAlign: 'center',
          }}
        >
          {textContent}
        </div>
      </div>
    )
  }
  
  // Default: simple text display (always shown when component is rendered)
  return (
    <div 
      className='d-flex justify-content-center align-items-center fw-bold py-1' 
      ref={ref}
    >
      {textContent}
    </div>
  )
})

ContinuedToAndFromDiv.displayName = 'ContinuedToAndFromDiv'

export default ContinuedToAndFromDiv
